import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Download,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  BarChart3,
  DollarSign,
  Truck,
  User,
  Plus,
  Search,
  Calendar,
  FileText,
  Eye,
  Edit2,
  Trash2
} from 'lucide-react';
import financialService from '../../services/financialService';
import expenseService from '../../services/expenseService';
import AddExpenseModal from './AddExpenseModal';
import { useFinancialRefresh } from '../../contexts/FinancialRefreshContext';
import FinancialNotification from '../ui/FinancialNotification';
import ConfirmationModal from '../ui/ConfirmationModal';

const DEFAULT_COST_STATS = {
  totalCost: 0,
  averageMonthly: 0,
  topCategory: { category: 'N/A', amount: 0 },
  categories: 0
};

const CATEGORY_COLORS = [
  '#EF4444', '#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#06B6D4', '#84CC16', '#EC4899', '#6366F1'
];

// Helper to get date ranges
const getDateRanges = () => {
  const now = new Date();
  const currentYear = now.getFullYear();

  // Use a wider range to capture all expenses, including future-dated ones
  // Start from current year and go to next year to handle any future-dated expenses
  const startDate = `${currentYear}-01-01`;
  const endDate = `${currentYear + 1}-12-31`;

  return { startDate, endDate };
};

const CostsTab = ({ formatCurrency }) => {
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('category'); // 'category', 'truck', 'driver'
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const { refreshTriggers, onExpenseCreated } = useFinancialRefresh();
  const [financialNotification, setFinancialNotification] = useState(null);
  const [isSavingExpense, setIsSavingExpense] = useState(false);
  const [expenseFormError, setExpenseFormError] = useState(null);

  // API data
  const [costData, setCostData] = useState(null);
  const [individualExpenses, setIndividualExpenses] = useState([]);
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showExpenseDetails, setShowExpenseDetails] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, expenseId: null, expenseInfo: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedCompany = window.localStorage.getItem('company');
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        setCompanyId(parsed?.id || null);
      }
    } catch (err) {
      console.warn('Unable to parse company from localStorage', err);
    }
  }, []);

  const fetchCostData = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      setError('Company context missing. Select a company to view costs.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dateRanges = getDateRanges();

      // Fetch cost breakdown from Phase 6 API
      const response = await financialService.getCostBreakdown({
        startDate: dateRanges.startDate,
        endDate: dateRanges.endDate,
        groupBy: view
      });

      console.log('CostsTab - API response:', response);
      console.log('CostsTab - response.breakdown:', response?.breakdown);
      console.log('CostsTab - response.grandTotal:', response?.grandTotal);
      setCostData(response);
    } catch (err) {
      console.error('Failed to load cost data:', err);
      setError('Unable to load cost breakdown. Please try again.');
      setCostData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, view]);

  const fetchIndividualExpenses = useCallback(async () => {
    if (!companyId) return;

    setLoadingExpenses(true);
    try {
      const dateRanges = getDateRanges();

      // Fetch individual expense list
      const response = await expenseService.listExpenses({
        startDate: dateRanges.startDate,
        endDate: dateRanges.endDate,
        limit: 100 // Get recent expenses
      });

      setIndividualExpenses(response?.expenses || response || []);
    } catch (err) {
      console.error('Failed to load individual expenses:', err);
      setIndividualExpenses([]);
    } finally {
      setLoadingExpenses(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchCostData();
    fetchIndividualExpenses();
  }, [companyId, fetchCostData, fetchIndividualExpenses]);

  // Refresh data when triggered by financial changes
  useEffect(() => {
    if (!companyId) return;
    if (refreshTriggers.costs > 0 || refreshTriggers.all > 0) {
      // Debounce refresh to avoid multiple rapid updates
      const timer = setTimeout(() => {
        fetchCostData();
        fetchIndividualExpenses();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [refreshTriggers.costs, refreshTriggers.all, companyId, fetchCostData, fetchIndividualExpenses]);

  // Get the appropriate breakdown based on view
  const breakdownData = useMemo(() => {
    if (!costData) return [];

    // The backend returns 'breakdown' array for all views
    // Not byCategory, byVehicle, or byDriver
    const data = costData.breakdown || [];
    console.log('CostsTab - breakdown data:', data);
    return data;
  }, [costData]);

  // Sort breakdown data
  const sortedData = useMemo(() => {
    let sortable = [...breakdownData];

    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested values
        if (sortConfig.key === 'name') {
          aValue = a.dimensionValue || a.category || a.vehicle || a.driver || '';
          bValue = b.dimensionValue || b.category || b.vehicle || b.driver || '';
        } else if (sortConfig.key === 'total') {
          aValue = a.totalAmount || 0;
          bValue = b.totalAmount || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortable;
  }, [breakdownData, sortConfig]);

  // Filter individual expenses based on search term
  const filteredExpenses = useMemo(() => {
    if (!expenseSearchTerm) return individualExpenses;

    const searchLower = expenseSearchTerm.toLowerCase();
    return individualExpenses.filter(expense => {
      return (
        expense.category?.toLowerCase().includes(searchLower) ||
        expense.vendor?.toLowerCase().includes(searchLower) ||
        expense.description?.toLowerCase().includes(searchLower) ||
        expense.expenseNumber?.toLowerCase().includes(searchLower) ||
        expense.amount?.toString().includes(searchLower)
      );
    });
  }, [individualExpenses, expenseSearchTerm]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleViewExpense = (expense) => {
    setSelectedExpense(expense);
    setShowExpenseDetails(true);
  };

  const handleDeleteExpense = (expense) => {
    setDeleteConfirmation({
      isOpen: true,
      expenseId: expense.id,
      expenseInfo: {
        number: expense.expenseNumber,
        category: expense.category,
        amount: expense.totalAmount || expense.amount,
        vendor: expense.vendor
      }
    });
  };

  const confirmDeleteExpense = async () => {
    if (!deleteConfirmation.expenseId) return;

    setIsDeleting(true);
    try {
      await expenseService.deleteExpense(deleteConfirmation.expenseId);

      // Show success notification
      setFinancialNotification({
        type: 'expense_deleted',
        title: 'Expense Deleted',
        message: `Expense ${deleteConfirmation.expenseInfo?.number || ''} has been successfully deleted`,
        financialImpact: null
      });

      // Close modal
      setDeleteConfirmation({ isOpen: false, expenseId: null, expenseInfo: null });

      // Refresh data
      await fetchCostData();
      await fetchIndividualExpenses();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      setError('Failed to delete expense. Please try again.');
      // Close modal even on error
      setDeleteConfirmation({ isOpen: false, expenseId: null, expenseInfo: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';

    // Handle different date formats
    let dateObj;
    if (date?.toDate) {
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return 'N/A';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSaveExpense = async (expenseData) => {
    setIsSavingExpense(true);
    setExpenseFormError(null);

    try {
      // Create the expense using the service
      const result = await expenseService.createExpense(expenseData);

      // Trigger financial refresh
      onExpenseCreated({
        ...result,
        category: expenseData.category,
        amount: expenseData.totalAmount || expenseData.amount
      });

      // Show success notification
      setFinancialNotification({
        type: 'expense_created',
        title: 'Expense Added',
        message: `${expenseData.category} expense of ${formatCurrency(expenseData.totalAmount)} has been recorded`,
        financialImpact: {
          expense: expenseData.totalAmount,
          netImpact: -(expenseData.totalAmount)
        }
      });

      // Close modal and refresh data
      setShowAddExpenseModal(false);
      setExpenseFormError(null);
      await fetchCostData(); // Refresh the cost breakdown
      await fetchIndividualExpenses(); // Refresh individual expenses

    } catch (error) {
      console.error('Failed to create expense:', error);

      // Parse validation errors for field highlighting
      let errorDetails = {};
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        error.response.data.details.forEach(detail => {
          if (detail.field) {
            errorDetails[detail.field] = detail.message;
          }
        });
      }

      setExpenseFormError({
        message: error.response?.data?.error || error.message || 'Failed to create expense. Please try again.',
        fields: errorDetails
      });
    } finally {
      setIsSavingExpense(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Total Cost', 'Percentage'];
    const rows = sortedData.map(item => [
      item.dimensionValue || item.category || item.vehicle || item.driver,
      item.totalAmount || 0,
      item.percentage || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-breakdown-${view}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="w-4 h-4 text-gray-600" /> :
      <ChevronDown className="w-4 h-4 text-gray-600" />;
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold ${color === 'blue' ? 'text-blue-600' : 'text-gray-900'}`}>
            {typeof value === 'number' ? formatCurrency(value) : value}
          </p>
        </div>
        {Icon && <Icon className={`w-8 h-8 ${color === 'blue' ? 'text-blue-600' : 'text-gray-400'}`} />}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-3"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="h-80 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
        <div className="flex items-center justify-between text-red-700">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5" />
            <p>{error}</p>
          </div>
          <button type="button" className="text-sm font-medium underline" onClick={fetchCostData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!costData) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <p className="text-sm text-gray-600">No cost data available yet.</p>
      </div>
    );
  }

  const totalCosts = costData.grandTotal || costData.totalCosts || 0;
  const topCategory = sortedData.length > 0 ? sortedData[0] : null;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Costs (This Year)"
          value={totalCosts}
          icon={DollarSign}
          color="blue"
        />
        <StatCard
          title={`Top ${view === 'category' ? 'Category' : view === 'vehicle' ? 'Truck' : 'Driver'}`}
          value={topCategory ? (topCategory.dimensionValue || topCategory.category || topCategory.vehicle || topCategory.driver) : 'N/A'}
          icon={view === 'vehicle' ? Truck : view === 'driver' ? User : BarChart3}
        />
        <StatCard
          title="Cost Items"
          value={sortedData.length}
          icon={BarChart3}
        />
      </div>

      {/* View Selector & Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
            <label className="text-sm font-medium text-gray-700">View By:</label>
            <select
              value={view}
              onChange={(e) => setView(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="category">Category</option>
              <option value="vehicle">Truck</option>
              <option value="driver">Driver</option>
            </select>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setShowAddExpenseModal(true)}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              <span>Add Expense</span>
            </button>
            <button
              onClick={exportToCSV}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Individual Expenses List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Individual Expenses</h3>
            <div className="flex items-center space-x-2">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search expenses..."
                  value={expenseSearchTerm}
                  onChange={(e) => setExpenseSearchTerm(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Expenses - Responsive Layout */}
          {loadingExpenses ? (
            <div className="text-center py-4">
              <p className="text-gray-500">Loading expenses...</p>
            </div>
          ) : filteredExpenses.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="sm:hidden space-y-3 max-h-96 overflow-y-auto">
                {filteredExpenses.map((expense) => (
                  <div key={expense.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-900">{expense.expenseNumber || 'N/A'}</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(expense.totalAmount || expense.amount)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {formatDate(expense.expenseDate || expense.date)}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1 mb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Category:</span>
                        <span className="text-xs font-medium text-gray-900">{expense.category}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Vendor:</span>
                        <span className="text-xs text-gray-700">{expense.vendor || 'N/A'}</span>
                      </div>
                      {expense.description && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 line-clamp-2">{expense.description}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-end space-x-3 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => handleViewExpense(expense)}
                        className="text-sm text-blue-600 hover:text-blue-900 font-medium flex items-center space-x-1"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => handleDeleteExpense(expense)}
                        className="text-sm text-red-600 hover:text-red-900 font-medium flex items-center space-x-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredExpenses.map((expense) => (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {formatDate(expense.expenseDate || expense.date)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {expense.expenseNumber || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {expense.category}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {expense.vendor || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          <span className="truncate block max-w-xs" title={expense.description}>
                            {expense.description || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                          {formatCurrency(expense.totalAmount || expense.amount)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleViewExpense(expense)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteExpense(expense)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete expense"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                {expenseSearchTerm
                  ? 'No expenses found matching your search'
                  : 'No expenses recorded yet'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Cost Breakdown Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Cost Breakdown by {view === 'category' ? 'Category' : view === 'vehicle' ? 'Truck' : 'Driver'}
          </h3>
          <p className="text-sm text-gray-600 mt-1">Year to date</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>{view === 'category' ? 'Category' : view === 'vehicle' ? 'Truck' : 'Driver'}</span>
                    <SortIcon column="name" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total')}
                >
                  <div className="flex items-center justify-end space-x-1">
                    <span>Total Cost</span>
                    <SortIcon column="total" />
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visual
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData.map((item, index) => {
                const name = item.dimensionValue || item.category || item.vehicle || item.driver || 'Unknown';
                const amount = item.totalAmount || 0;
                const percentage = item.percentage || (totalCosts > 0 ? (amount / totalCosts) * 100 : 0);

                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                        />
                        <span>{name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {formatCurrency(amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                      {percentage.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <div className="flex items-center justify-end">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* Totals Row */}
              {sortedData.length > 0 && (
                <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    TOTAL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(totalCosts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    100.0%
                  </td>
                  <td className="px-6 py-4"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {sortedData.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            No cost data available for the selected view.
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      {showAddExpenseModal && (
        <AddExpenseModal
          isOpen={showAddExpenseModal}
          onClose={() => {
            setShowAddExpenseModal(false);
            setExpenseFormError(null);
          }}
          onSave={handleSaveExpense}
          formatCurrency={formatCurrency}
          isSubmitting={isSavingExpense}
          error={expenseFormError}
        />
      )}

      {/* Financial Notification */}
      <FinancialNotification
        notification={financialNotification}
        onClose={() => setFinancialNotification(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, expenseId: null, expenseInfo: null })}
        onConfirm={confirmDeleteExpense}
        title="Delete Expense"
        message={
          deleteConfirmation.expenseInfo
            ? `Are you sure you want to delete expense ${deleteConfirmation.expenseInfo.number}? This action cannot be undone.

               Expense Details:
               • Category: ${deleteConfirmation.expenseInfo.category}
               • Vendor: ${deleteConfirmation.expenseInfo.vendor || 'N/A'}
               • Amount: ${formatCurrency(deleteConfirmation.expenseInfo.amount)}`
            : 'Are you sure you want to delete this expense? This action cannot be undone.'
        }
        confirmText={isDeleting ? "Deleting..." : "Delete Expense"}
        cancelText="Cancel"
        type="danger"
        isDestructive={true}
      />

      {/* Expense Details Modal */}
      {showExpenseDetails && selectedExpense && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Expense Details</h3>
              <button
                onClick={() => {
                  setShowExpenseDetails(false);
                  setSelectedExpense(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm font-medium text-gray-600">Expense Number</p>
                <p className="text-base text-gray-900">{selectedExpense.expenseNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Date</p>
                <p className="text-base text-gray-900">{formatDate(selectedExpense.expenseDate || selectedExpense.date)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Category</p>
                <p className="text-base text-gray-900">{selectedExpense.category}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Subcategory</p>
                <p className="text-base text-gray-900">{selectedExpense.subcategory || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Vendor</p>
                <p className="text-base text-gray-900">{selectedExpense.vendor || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatCurrency(selectedExpense.totalAmount || selectedExpense.amount)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Payment Method</p>
                <p className="text-base text-gray-900">
                  {selectedExpense.paymentMethod ?
                    selectedExpense.paymentMethod.replace('_', ' ').charAt(0).toUpperCase() +
                    selectedExpense.paymentMethod.slice(1).replace('_', ' ')
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <p className="text-base text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full
                    ${selectedExpense.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedExpense.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'}`}>
                    {selectedExpense.status || 'N/A'}
                  </span>
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm font-medium text-gray-600 mb-1">Description</p>
              <p className="text-base text-gray-900">{selectedExpense.description || 'No description provided'}</p>
            </div>

            {selectedExpense.notes && (
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-600 mb-1">Notes</p>
                <p className="text-base text-gray-900">{selectedExpense.notes}</p>
              </div>
            )}

            {(selectedExpense.vehicleId || selectedExpense.driverId) && (
              <div className="grid grid-cols-2 gap-4 mb-6 border-t pt-4">
                {selectedExpense.vehicleId && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Vehicle</p>
                    <p className="text-base text-gray-900">{selectedExpense.vehicleId}</p>
                  </div>
                )}
                {selectedExpense.driverId && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Driver</p>
                    <p className="text-base text-gray-900">{selectedExpense.driverId}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowExpenseDetails(false);
                  setSelectedExpense(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostsTab;
